import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getEnrollments } from "../../../services/EnrollmentService";
import { getAcademicYears } from "../../../services/AcademicYearService";
import { pdf } from "@react-pdf/renderer";
import { getAnnualTranscriptData } from "../../../services/ExamResultService";
import AnnualTranscriptPDF from "./AnnualTranscriptPDF";
import { canReadTranscripts, canExportTranscripts } from "../../../utils/permissions";

const fmt = (n) => (Number.isFinite(Number(n)) ? Number(n).toFixed(2) : "-");

// Relevé de notes annuel exporté depuis le profil de l'étudiant. Modules et
// matières viennent de l'inscription (classe) de l'année universitaire choisie ;
// une matière sans examen passé apparaît avec la note 0.
const AnnualTranscriptCard = ({ studentId }) => {
  const { t } = useTranslation();
  // Consulter et exporter sont deux permissions distinctes (TRANSCRIPT_READ_* / TRANSCRIPT_EXPORT_*).
  const user = useSelector((state) => state.auth.auth?.user);
  const canRead = canReadTranscripts(user);
  const canExport = canExportTranscripts(user);
  const [years, setYears] = useState([]);
  const [yearId, setYearId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!studentId) return;
    Promise.all([
      getEnrollments({ page: 1, limit: 100, student_id: studentId }).catch(() => null),
      getAcademicYears({ page: 1, limit: 200 }).catch(() => null),
    ]).then(([enrollRes, yearsRes]) => {
      const allYears = yearsRes?.data?.data || [];
      const enrolledIds = new Set((enrollRes?.data?.data || []).map((e) => e.academic_year_id));
      // Années où l'étudiant est inscrit ; à défaut, toutes les années.
      const list = enrolledIds.size ? allYears.filter((y) => enrolledIds.has(y.id)) : allYears;
      setYears(list);
      if (list.length) setYearId(list[0].id);
    });
  }, [studentId]);

  useEffect(() => { setData(null); setError(""); }, [yearId]);

  const preview = async () => {
    if (!yearId) return;
    setLoading(true);
    setError("");
    try {
      const res = await getAnnualTranscriptData(studentId, yearId);
      setData(res.data?.data || null);
    } catch (err) {
      setData(null);
      setError(err?.response?.data?.message || t("pages.studentDetail.annualTranscriptError"));
    } finally {
      setLoading(false);
    }
  };

  // Le PDF reprend la présentation du relevé de notes officiel (une page par semestre).
  const exportPdf = async () => {
    if (!yearId) return;
    setExporting(true);
    setError("");
    try {
      // purpose "export" : exige TRANSCRIPT_EXPORT_* et journalise l'export côté serveur.
      const res = await getAnnualTranscriptData(studentId, yearId, { purpose: "export" });
      const transcript = res.data?.data ?? res.data;
      if (!transcript) throw new Error("empty");
      const blob = await pdf(<AnnualTranscriptPDF data={transcript} />).toBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `releve-de-notes-annuel-${transcript.student?.registrationNumber || studentId}-${years.find((y) => y.id === yearId)?.name || "annee"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || t("pages.studentDetail.annualTranscriptError"));
    } finally {
      setExporting(false);
    }
  };

  if (!canRead) return null;

  return (
    <div className="card mb-3">
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <h5 className="mb-0">{t("pages.studentDetail.annualTranscript")}</h5>
        <div className="d-flex flex-wrap gap-2">
          <select className="form-select w-auto" value={yearId} onChange={(e) => setYearId(e.target.value)}>
            {years.map((y) => (<option key={y.id} value={y.id}>{y.name}</option>))}
          </select>
          <button type="button" className="btn btn-outline-primary" onClick={preview} disabled={!yearId || loading}>
            {loading ? t("pages.studentDetail.annualTranscriptLoading") : t("pages.studentDetail.annualTranscriptPreview")}
          </button>
          {canExport && (
            <button type="button" className="btn btn-primary" onClick={exportPdf} disabled={!yearId || exporting}>
              <i className="ti ti-file-export me-2"></i>
              {exporting ? t("pages.studentDetail.annualTranscriptExporting") : t("pages.studentDetail.annualTranscriptExport")}
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {years.length === 0 && <p className="text-muted mb-0">{t("pages.studentDetail.annualTranscriptNoYear")}</p>}
        {error && <div className="alert alert-warning mb-0">{error}</div>}
        {data && (
          <>
            <p className="text-muted small">
              {data.class?.name} ({data.class?.code}) · {data.department} · {data.academicYear?.name}
            </p>
            {data.includesProvisional && (
              <div className="alert alert-warning py-2">
                {t("pages.studentDetail.annualTranscriptProvisional", {
                  defaultValue: "Ce relevé contient des résultats provisoires (non publiés) : il ne peut pas être remis comme document officiel.",
                })}
              </div>
            )}
            {data.semesters.length === 0 && <p className="text-muted">{t("pages.studentDetail.annualTranscriptNoModules")}</p>}
            {data.semesters.map((semester) => (
              <div className="mb-3" key={semester.id || semester.name}>
                <h6>{semester.name}</h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered align-middle">
                    <thead>
                      <tr className="text-center">
                        <th className="text-start">{t("pages.studentDetail.moduleElement")}</th>
                        <th>EXAM</th>
                        <th>NFE</th>
                        <th>{t("pages.modules.credits")}</th>
                        <th>{t("pages.statistics.decisionColumn")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {semester.modules.map((m) => (
                        <React.Fragment key={m.id}>
                          <tr className="table-secondary fw-bold">
                            <td>{m.code} - {m.name}</td>
                            <td colSpan="3" className="text-center">{Number(m.average).toFixed(3).replace(".", ",")}</td>
                            <td></td>
                          </tr>
                          {m.subjects.map((s) => (
                            <tr key={s.id} className="text-center">
                              <td className="text-start ps-4">
                                {s.name}{!s.hasExam && <span className="text-muted"> ({t("pages.studentDetail.noExam")})</span>}
                              </td>
                              <td>{fmt(s.score)}</td>
                              <td>{fmt(s.score)}</td>
                              <td>{s.credits}</td>
                              <td className="fw-bold">{s.decision}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                      <tr className="fw-bold text-center">
                        <td className="text-start">{t("pages.studentDetail.semesterAverage")} | 20</td>
                        <td colSpan="3">{Number(semester.average).toFixed(3).replace(".", ",")}</td>
                        <td>{semester.validated ? t("pages.studentDetail.decisionValidated") : t("pages.studentDetail.decisionNotValidated")}</td>
                      </tr>
                      <tr className="fw-bold text-center">
                        <td className="text-start">Credits | {semester.credits}</td>
                        <td colSpan="3">{Number(semester.creditsAcquired).toFixed(2).replace(".", ",")}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {data.semesters.length > 0 && (
              <p className="fw-bold mb-0">
                {t("pages.studentDetail.annualAverage")} : {fmt(data.average)} / 20
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AnnualTranscriptCard;